import { FetchParentInformationResponse } from "@seneca/parents-service-types";

import {
  useTypedSearchParams,
  useTypedState
} from "seneca-common/utils/routing/react-router-typesafe-routes/dom";

import { ROUTES } from "routing/routes";

import { useParentDataPrefill } from "../hooks";

export enum CardState {
  loading = "loading",
  showPasswordSignUp = "showPasswordSignUp",
  showPasswordlessSignUp = "showPasswordlessSignUp",
  default = "default"
}

export function useGetSignUpCardState() {
  const { email: emailFromState, errorMessage } = useTypedState(
    ROUTES.SIGN_UP_PARENT
  );

  const { hasConnectionKey, parentData, isParentDataLoading } =
    useParentDataPrefill();

  const [{ showPasswordSignUp }] = useTypedSearchParams(ROUTES.SIGN_UP_PARENT);

  const result = getSignUpCardState({
    isParentDataLoading,
    parentData,
    showPasswordSignUp,
    hasConnectionKey
  });

  return { ...result, emailFromState, errorMessage };
}

type Args = {
  isParentDataLoading: boolean;
  parentData: FetchParentInformationResponse | undefined;
  showPasswordSignUp: boolean;
  hasConnectionKey: boolean;
};

type SignUpCardStateResponse =
  | {
      state:
        | CardState.loading
        | CardState.default
        | CardState.showPasswordSignUp;
      data?: FetchParentInformationResponse;
    }
  | {
      state: CardState.showPasswordlessSignUp;
      data: FetchParentInformationResponse & { email: string };
    };

export function getSignUpCardState({
  isParentDataLoading,
  parentData,
  showPasswordSignUp,
  hasConnectionKey
}: Args): SignUpCardStateResponse {
  if (isParentDataLoading) {
    return { state: CardState.loading };
  }

  if (showPasswordSignUp) {
    return { state: CardState.showPasswordSignUp, data: parentData };
  }

  if (hasConnectionKey && parentDataHasEmail(parentData)) {
    return { state: CardState.showPasswordlessSignUp, data: parentData };
  }

  return { state: CardState.default };
}

function parentDataHasEmail<T extends { email?: string }>(
  data?: T
): data is T & { email: string } {
  return !!data?.email;
}
